import { APP_STRINGS, PR_FILTER_RANGES } from '../constants';
import { sortStandupSprintDropdownList } from './capacityPlanningUtils';
import { resolveIssueScopeFlags } from '../components/Common/standUpCommonFunctions';
import { isFirstPassApprovedPR } from './firstPassPrUtils';

export function toIdString(id) {
  if (id === null || id === undefined) return '';
  return String(id);
}

export function getProjectById(filters, projectId) {
  if (!filters?.projects) return null;
  const key = toIdString(projectId);
  return filters.projects.find((p) => toIdString(p._id) === key) || null;
}

/**
 * Keep multiproject filters aligned with nav-accessible projects
 * (same permission list as Standup single-project dropdown).
 * If accessibleProjects is empty/unavailable, return filters unchanged (API already scoped).
 */
export function scopeMultiProjectFiltersByAccessibleProjects(filters, accessibleProjects = []) {
  if (!filters?.projects?.length) return filters;
  const accessibleIds = new Set(
    (accessibleProjects || [])
      .map((p) => toIdString(p?._id || p?.projectId || p?.id))
      .filter(Boolean),
  );
  if (!accessibleIds.size) return filters;

  const projects = filters.projects.filter((p) => accessibleIds.has(toIdString(p._id)));
  const visible = new Set(projects.map((p) => toIdString(p._id)));
  return {
    ...filters,
    projects,
    sprints: (filters.sprints || []).filter((s) => visible.has(toIdString(s.projectId))),
    releases: (filters.releases || []).filter((r) => visible.has(toIdString(r.projectId))),
  };
}

export function getSelectedProjectIds(selectedProjects = []) {
  if (!Array.isArray(selectedProjects)) return [];
  return selectedProjects
    .map((entry) => (typeof entry === 'string' ? toIdString(entry) : toIdString(entry?.projectId)))
    .filter(Boolean);
}

export function normalizeSelectedProject(entry, filters) {
  if (!entry) return null;

  const projectId =
    typeof entry === 'string' ? toIdString(entry) : toIdString(entry?.projectId ?? entry?.id);
  if (!projectId) return null;

  const project = getProjectById(filters, projectId);
  if (!project) return null;

  let boardId = typeof entry === 'string' ? '' : toIdString(entry?.boardId);
  const projectBoards = project.boards || [];
  const boardIdSet = new Set(projectBoards.map((board) => toIdString(board._id)));

  if (!boardId || !boardIdSet.has(boardId)) {
    boardId = toIdString(project.defaultBoardId) || toIdString(projectBoards[0]?._id) || null;
  }

  return { projectId, boardId };
}

export function normalizeSelectedProjects(entries = [], filters) {
  if (!Array.isArray(entries)) return [];
  const seen = new Map();
  entries.forEach((entry) => {
    const normalized = normalizeSelectedProject(entry, filters);
    if (normalized) {
      seen.set(normalized.projectId, normalized);
    }
  });
  return [...seen.values()];
}

export function getProjectDocsFromSelections(filters, selectedProjects = []) {
  return normalizeSelectedProjects(selectedProjects, filters)
    .map(({ projectId }) => getProjectById(filters, projectId))
    .filter(Boolean);
}

export function getReposForProjects(projects = []) {
  const repoSet = new Set();
  projects.forEach((project) => {
    (project?.repos || []).forEach((repo) => {
      if (repo) repoSet.add(repo);
    });
  });
  return [...repoSet].sort();
}

export function getSprintsForProjects(filters, projectIds = []) {
  if (!filters?.sprints || !projectIds.length) return [];
  const idSet = new Set(projectIds.map(toIdString));
  const sprints = filters.sprints.filter((s) => idSet.has(toIdString(s.projectId)));
  return sortStandupSprintDropdownList(sprints);
}

export function getReleasesForProjects(filters, projectIds = []) {
  if (!filters?.releases || !projectIds.length) return [];
  const idSet = new Set(projectIds.map(toIdString));
  return filters.releases.filter((r) => idSet.has(toIdString(r.projectId)));
}

export function buildMultiProjectIssuesPayload({
  selectedProjects = [],
  selectedSprintIds = [],
  selectedReleaseIds = [],
  filterMode = null,
  filters,
}) {
  const normalized = normalizeSelectedProjects(selectedProjects, filters);
  const projectIds = normalized.map(({ projectId }) => projectId);
  const boardIds = normalized.map(({ boardId }) => boardId).filter(Boolean);

  if (filterMode === 'backlog') {
    return {
      projectIds,
      boardIds,
      backlog: true,
    };
  }

  return {
    projectIds,
    boardIds,
    sprintIds: selectedSprintIds.map(toIdString).filter(Boolean),
    releaseIds: selectedReleaseIds.map(toIdString).filter(Boolean),
  };
}

export function resolveDefaultMultiProjectSelections({
  filters,
  persistedProjects = [],
  persistedSprintIds = [],
  persistedReleaseIds = [],
  persistedRepos = [],
  persistedFilterMode = null,
}) {
  const standupProjectId = sessionStorage.getItem('projectId');
  const standupBoardId = sessionStorage.getItem('boardId');
  const standupSprintId = sessionStorage.getItem('sprintId');
  const standupReleaseId = sessionStorage.getItem('releaseId');
  const typeValue = sessionStorage.getItem(APP_STRINGS.SESSION_TYPE_VALUE);

  let selectedProjects = persistedProjects.length
    ? normalizeSelectedProjects(persistedProjects, filters)
    : standupProjectId
      ? [
          {
            projectId: toIdString(standupProjectId),
            boardId: standupBoardId ? toIdString(standupBoardId) : null,
          },
        ]
      : [];

  selectedProjects = normalizeSelectedProjects(selectedProjects, filters);

  if (!selectedProjects.length && filters?.projects?.length === 1) {
    const onlyProject = filters.projects[0];
    selectedProjects = [
      {
        projectId: toIdString(onlyProject._id),
        boardId: toIdString(onlyProject.defaultBoardId) || null,
      },
    ];
  }

  const selectedProjectIds = getSelectedProjectIds(selectedProjects);

  let selectedSprintIds = persistedSprintIds.map(toIdString);
  let selectedReleaseIds = persistedReleaseIds.map(toIdString);
  let filterMode = persistedFilterMode;

  const availableSprints = getSprintsForProjects(filters, selectedProjectIds);
  const availableReleases = getReleasesForProjects(filters, selectedProjectIds);
  const sprintIdSet = new Set(availableSprints.map((s) => toIdString(s._id)));
  const releaseIdSet = new Set(availableReleases.map((r) => toIdString(r._id)));

  selectedSprintIds = selectedSprintIds.filter((id) => sprintIdSet.has(id));
  selectedReleaseIds = selectedReleaseIds.filter((id) => releaseIdSet.has(id));

  if (!selectedSprintIds.length && !selectedReleaseIds.length && selectedProjectIds.length === 1) {
    if (typeValue === APP_STRINGS.VALUE_BACKLOG) {
      filterMode = 'backlog';
    } else if (
      typeValue === APP_STRINGS.VALUE_RELEASE &&
      standupReleaseId &&
      releaseIdSet.has(toIdString(standupReleaseId))
    ) {
      selectedReleaseIds = [toIdString(standupReleaseId)];
      filterMode = 'release';
    } else if (standupSprintId && sprintIdSet.has(toIdString(standupSprintId))) {
      selectedSprintIds = [toIdString(standupSprintId)];
      filterMode = 'sprint';
    }
  }

  if (selectedSprintIds.length && selectedReleaseIds.length) {
    if (filterMode === 'release') {
      selectedSprintIds = [];
    } else {
      selectedReleaseIds = [];
      filterMode = 'sprint';
    }
  } else if (selectedSprintIds.length) {
    filterMode = 'sprint';
  } else if (selectedReleaseIds.length) {
    filterMode = 'release';
  } else if (
    filterMode === 'backlog' ||
    persistedFilterMode === 'backlog' ||
    typeValue === APP_STRINGS.VALUE_BACKLOG
  ) {
    filterMode = 'backlog';
  }

  const projects = getProjectDocsFromSelections(filters, selectedProjects);
  const allRepos = getReposForProjects(projects);
  let selectedRepos = persistedRepos.filter((repo) => allRepos.includes(repo));
  if (!selectedRepos.length && allRepos.length) {
    selectedRepos = [...allRepos];
  }

  return {
    selectedProjects,
    selectedSprintIds,
    selectedReleaseIds,
    selectedRepos,
    filterMode,
  };
}

export function computeStatusBadgeData(issues = []) {
  const counts = new Map();
  issues.forEach((row) => {
    const status = String(row?.status ?? 'No Status').trim() || 'No Status';
    counts.set(status, (counts.get(status) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
}

export function filterIssuesByStatus(issues = [], selectedStatuses = []) {
  if (!selectedStatuses.length) return issues;
  return issues.filter((row) => selectedStatuses.includes(row.status));
}

export function filterPRsByRepos(prsData = [], selectedRepos = []) {
  if (!selectedRepos.length) return [];
  return prsData.filter((row) =>
    selectedRepos.some((repo) => {
      const shortRepo = repo.split('/').pop() || repo;
      return row?.repo?.toLowerCase() === shortRepo.toLowerCase();
    }),
  );
}

export function shouldShowMultiProjectPRTable(
  selectedSprintIds = [],
  selectedReleaseIds = [],
  filterMode = null,
) {
  if (filterMode === 'backlog') return false;
  return selectedSprintIds.length <= 1 && selectedReleaseIds.length <= 1;
}

function resolveStandupEstimateDisplay(issue) {
  const sp = issue?.storyPoints;
  if (sp === null || sp === undefined || sp === '' || sp === 'NA') {
    return 'NA';
  }
  const n = Number(sp);
  return Number.isFinite(n) ? n : 'NA';
}

/** Map multi-project / standup API issue shape to AG Grid row (same as StandUpPage). */
export function mapJiraApiIssueToStandupRow(issue) {
  if (!issue) return null;
  const pullRequests = Array.isArray(issue.pullRequests) ? issue.pullRequests : [];
  const prId = pullRequests.length ? pullRequests.map((pr) => pr.prNumber).join(', ') : 'NA';
  return {
    jiraTicket: issue.key,
    type: issue.type !== 'NA' ? issue.type : 'NA',
    prId,
    dueDate:
      issue.duedate !== 'NA' && issue.duedate != null
        ? new Date(issue.duedate).toISOString().split('T')[0]
        : 'No Due Date',
    sprintOutcome: issue.sprintOutcome || { spilledOver: null, sprintNames: [] },
    status: issue.status || 'No Status',
    summary: issue.summary || 'No Summary',
    assignedTo: issue.assignee || 'Unassigned',
    blocker: issue.blockedBy || 'No blocker',
    priority: issue.priority,
    severity: issue.severity ?? 'NA',
    jiraUrl: issue.jiraUrl,
    storyPoints: resolveStandupEstimateDisplay(issue),
    originalEstimate: issue.originalEstimate || 0,
    developer: issue.developer || issue.assignee || 'Unassigned',
    epic: issue.epic || 'No Epic',
    labels: issue.labels || 'No Label',
    timeSpent: issue.timeSpent || 0,
    cycleTime: issue.cycleTime || '0d',
    backflowRate: issue.backflowRate || 0,
    customFields: issue.customFields || {},
    customFieldsByName: issue.customFieldsByName || {},
    scopeFlags: resolveIssueScopeFlags(issue),
  };
}

export function flattenIssuesToPullRequests(issues = []) {
  return issues.flatMap((issue) => (Array.isArray(issue.pullRequests) ? issue.pullRequests : []));
}

export function mapMultiProjectPrToGridRow(pr) {
  if (!pr) return null;

  let daysOpenValue = 0;
  let daysOpenDisplay = 'NA';
  if (pr.daysOpen !== undefined && pr.daysOpen !== null && pr.daysOpen !== 'NA') {
    if (typeof pr.daysOpen === 'number') {
      daysOpenValue = pr.daysOpen;
      daysOpenDisplay = `${daysOpenValue}`;
    } else if (typeof pr.daysOpen === 'string') {
      const match = pr.daysOpen.match(/\d+/);
      if (match) {
        daysOpenValue = parseInt(match[0], 10);
        daysOpenDisplay = pr.daysOpen;
      }
    }
  } else if (pr.createdAt || pr.prCreatedAt || pr.created_at) {
    const createdAt = pr.createdAt || pr.prCreatedAt || pr.created_at;
    const createdDate = new Date(createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);
    const diffTime = today - createdDate;
    daysOpenValue = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    daysOpenDisplay = daysOpenValue >= 0 ? `${daysOpenValue}` : '0';
  }

  const reviewerCount = pr.prReviews?.length ?? 0;
  const reviewerText =
    reviewerCount > 0 ? pr.prReviews.map((r) => r.reviewerUsername).join(', ') : 'NA';

  let reviewerRange = PR_FILTER_RANGES.REVIEWER.NO_REVIEWERS;
  if (reviewerCount === 1) {
    reviewerRange = PR_FILTER_RANGES.REVIEWER.ONE_REVIEWER;
  } else if (reviewerCount >= 2 && reviewerCount <= 3) {
    reviewerRange = PR_FILTER_RANGES.REVIEWER.TWO_TO_THREE_REVIEWERS;
  } else if (reviewerCount >= 4) {
    reviewerRange = PR_FILTER_RANGES.REVIEWER.FOUR_PLUS_REVIEWERS;
  }

  const filesChanged = pr.filesChanged || 0;

  return {
    prId: pr.prNumber,
    title: pr.prTitle !== 'NA' ? pr.prTitle : 'No PR',
    author: pr.prCreatedBy || 'NA',
    status: pr.prStatus || 'No Status',
    daysOpen: daysOpenDisplay,
    daysOpenRange: daysOpenValue,
    daysOpenValue,
    reviewer: reviewerText,
    reviewerRange,
    reviewerCount,
    codeChanges: filesChanged,
    filesChangedRange: filesChanged,
    branch: pr.branchName !== 'NA' ? pr.branchName : 'No Branch',
    prUrl: pr.prUrl,
    repo: pr.repo || 'NA',
    merged: ['true', 'merged'].includes(pr?.merged?.toString()?.toLowerCase()) ? 'Yes' : 'No',
    isReviewed: Array.isArray(pr.prReviews) && pr.prReviews.length > 0,
    createdAtStandup: pr.standupPrCreatedAtDisplay ?? 'NA',
    mergedAtStandup: pr.standupPrMergedAtDisplay ?? 'NA',
    closedWithoutMergeAtStandup: pr.standupPrClosedWithoutMergeAtDisplay ?? 'NA',
    firstPassApproved:
      pr.firstPassApproved ??
      (Array.isArray(pr.prReviews) ? isFirstPassApprovedPR(pr.prReviews) : false),
  };
}

export function filterPrGridRowsByType(rows = [], prFilterType = 'all') {
  return rows.filter((pr) => {
    if (prFilterType === 'openReviewed') {
      return ['open', 'opened'].includes(pr.status) && pr.isReviewed;
    }
    if (prFilterType === 'openUnreviewed') {
      return ['open', 'opened'].includes(pr.status) && !pr.isReviewed;
    }
    if (prFilterType === 'merged') return pr.merged === 'Yes';
    if (prFilterType === 'totalOpenPRs') return ['open', 'opened'].includes(pr.status);
    if (prFilterType === 'closedPrs') {
      return pr.status === 'closed' && pr.merged === 'No';
    }
    if (prFilterType === 'firstPass') {
      return pr.isReviewed && pr.firstPassApproved;
    }
    return true;
  });
}

export function aggregateMultiProjectOpenPrData(prsData = [], selectedRepos = [], allRepos = []) {
  if (!selectedRepos.length) {
    return {};
  }

  const useAllRepos = selectedRepos.length === allRepos.length;
  const repoFiltered = useAllRepos
    ? prsData
    : prsData.filter((prGroup) =>
        selectedRepos.some((repo) => {
          const shortRepo = repo.split('/').pop() || repo;
          return prGroup?.repo?.toLowerCase() === shortRepo.toLowerCase();
        }),
      );

  if (!repoFiltered.length) {
    return {};
  }

  if (!useAllRepos && repoFiltered.length === 1) {
    return repoFiltered[0];
  }

  return repoFiltered.reduce((acc, prGroup) => {
    const firstPassPRCount = (acc.firstPassPRCount || 0) + (prGroup.firstPassPRCount || 0);
    const totalReviewedMergedPRs =
      (acc.totalReviewedMergedPRs || 0) + (prGroup.totalReviewedMergedPRs || 0);
    return {
      repo: useAllRepos ? 'All Repositories' : selectedRepos.join(', '),
      openReviewedPRs: (acc.openReviewedPRs || 0) + (prGroup.openReviewedPRs || 0),
      openUnreviewedPRs: (acc.openUnreviewedPRs || 0) + (prGroup.openUnreviewedPRs || 0),
      totalMergedPRs: (acc.totalMergedPRs || 0) + (prGroup.totalMergedPRs || 0),
      closedPrs: (acc.closedPrs || 0) + (prGroup.closedPrs || 0),
      totalPrs: (acc.totalPrs || 0) + (prGroup.totalPrs || 0),
      averageTimeToMergeForReviewedPRs:
        acc.averageTimeToMergeForReviewedPRs ||
        prGroup.averageTimeToMergeForReviewedPRs ||
        '0 hrs 0m',
      firstPassPRCount,
      totalReviewedMergedPRs,
      firstPassPRPercent:
        totalReviewedMergedPRs > 0
          ? Math.round((firstPassPRCount / totalReviewedMergedPRs) * 100)
          : 0,
    };
  }, {});
}

export function getMultiProjectFilterModeLabel(filterMode) {
  if (filterMode === 'release') return APP_STRINGS.VALUE_RELEASE;
  if (filterMode === 'sprint') return APP_STRINGS.VALUE_SPRINT;
  if (filterMode === 'backlog') return APP_STRINGS.VALUE_BACKLOG;
  return null;
}
